import { exactlyConfig } from '@1delta/data-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { EXACTLY_LENDER_KEY } from '../../public-data/exactly/convertPublic'
import { exactlyPenaltyRateToAprPercent } from '../../public-data/exactly/math'
import type {
  ExactlyFixedPosition,
  ExactlyMarketAccount,
} from '../../public-data/exactly/types'

const nowSec = () => Math.floor(Date.now() / 1000)
const DAY_SECONDS = 86_400n
const WAD = 10n ** 18n

/**
 * Per-position fixed-term detail attached to the position row (raw strings).
 *
 * Carries the FULL exit economics so a repay/withdraw UI needs no second read:
 * `faceValue` is what is owed/paid at maturity, `previewValue` is what the exit
 * actually costs/pays RIGHT NOW, and exactly one of `earlyRepayDiscount` /
 * `earlyExitCost` / `latePenalty` explains the gap. See the "repay terms"
 * section of the Exactly README for the source-verified formulas.
 */
export interface ExactlyUserFixedPosition {
  /** unix maturity */
  maturity: number
  /** 'deposit' | 'borrow' */
  kind: 'deposit' | 'borrow'
  /** face principal (raw asset units) */
  principal: string
  /** face fee locked at trade time (raw asset units) */
  fee: string
  /** face value at maturity = principal + fee. Static — Exactly fixed debt does
   *  NOT accrue an index; it only grows via the late penalty below. */
  faceValue: string
  /** live exit value now: withdraw-now / repay-now incl. discount or overdue
   *  penalty (raw asset units) — from the Previewer */
  previewValue: string
  /** true once maturity passed and the position is still open (borrows accrue
   *  the per-second late penalty until repaid) */
  overdue: boolean
  /** seconds past maturity (0 until overdue) */
  secondsLate: number
  /** BORROW before maturity: face − repay-now, the REBATE for repaying early
   *  (Exactly never charges an early-repay fee). Absent otherwise. */
  earlyRepayDiscount?: string
  /** DEPOSIT before maturity: face − payout-now, the HAIRCUT for exiting a
   *  fixed deposit early (sold back at the current curve rate). Absent
   *  otherwise. */
  earlyExitCost?: string
  /** BORROW past maturity: repay-now − face, penalty accrued SO FAR. Absent
   *  otherwise. */
  latePenalty?: string
  /** BORROW: penalty this position accrues per further day overdue (raw units,
   *  linear on face — not compounding). Present for borrows only. */
  latePenaltyPerDay: string
  /** market's linear late-penalty rate as an annualized percent (e.g. 164.24) */
  latePenaltyApr: number
}

function sumPreview(positions: readonly ExactlyFixedPosition[]): bigint {
  return positions.reduce((acc, p) => acc + p.previewValue, 0n)
}

/** face = principal + fee — the amount owed/paid AT maturity. */
const faceOf = (p: ExactlyFixedPosition) =>
  p.position.principal + p.position.fee

function toDetail(
  positions: readonly ExactlyFixedPosition[],
  kind: 'deposit' | 'borrow',
  now: number,
  penaltyRate: bigint,
  penaltyApr: number,
): ExactlyUserFixedPosition[] {
  return positions.map((p) => {
    const maturity = Number(p.maturity)
    const overdue = maturity < now
    const face = faceOf(p)
    // Signed gap between face and the live exit value. Early: previewValue <
    // face for BOTH sides (borrow = rebate you keep, deposit = haircut you
    // eat). Overdue: borrows only, previewValue > face by the penalty.
    const gap = face > p.previewValue ? face - p.previewValue : 0n
    const excess = p.previewValue > face ? p.previewValue - face : 0n
    return {
      maturity,
      kind,
      principal: p.position.principal.toString(),
      fee: p.position.fee.toString(),
      faceValue: face.toString(),
      previewValue: p.previewValue.toString(),
      overdue,
      secondsLate: overdue ? now - maturity : 0,
      ...(kind === 'borrow'
        ? {
            ...(overdue
              ? { latePenalty: excess.toString() }
              : { earlyRepayDiscount: gap.toString() }),
            // face × penaltyRate × 1 day — what another day of being late adds.
            latePenaltyPerDay: (
              (face * penaltyRate * DAY_SECONDS) /
              WAD
            ).toString(),
            latePenaltyApr: penaltyApr,
          }
        : {
            ...(overdue ? {} : { earlyExitCost: gap.toString() }),
            // Deposits carry no penalty: at/after maturity the payout is face.
            latePenaltyPerDay: '0',
            latePenaltyApr: 0,
          }),
    } as ExactlyUserFixedPosition
  })
}

/**
 * Returns a [converter, expectedCallCount] tuple for Exactly user data.
 *
 * Layout mirrors `buildExactlyUserCall`: ONE `Previewer.exactly(account)`
 * result → ONE `EXACTLY` bundle with one row PER ASSET.
 *
 * Exactly is cross-margin under a single per-chain Auditor (a Compound-V2
 * comptroller, not a Euler controller): entering a market flags a deposit in an
 * account-level bitmap and it then backs debt in ANY asset, with ONE global
 * health check. So the account maps 1:1 onto a single lender key with per-asset
 * rows — the same shape as Compound V2 or Aave — and the health engine gets the
 * Auditor's own formula from the per-asset factors the public data emits
 * (`collateralFactor = adjustFactor`, `borrowFactor = 1/adjustFactor`, so
 * Σ coll×adjF ≥ Σ debt/adjF). No collateral mirroring, no `crossCollateral`
 * dedupe rule, and no optimistic per-key health when debt spans several assets
 * — all three were artifacts of the retired `EXACTLY_<MARKET_ADDR>` split.
 *
 * Per asset row:
 *  - `deposits` = floating deposit + Σ fixed-deposit `previewValue`
 *    (withdraw-now value);
 *  - `debt` = floating borrow, `debtStable` = Σ fixed-borrow `previewValue`
 *    (repay-now value incl. overdue penalties) — the Aave-style split;
 *  - `fixedPositions` carries the per-maturity exit economics;
 *  - `collateralEnabled` = the Auditor's `enterMarket` flag for that asset.
 *
 * Each fixed borrow additionally emits a `${uid}#${maturity}` row carrying the
 * `term` metadata the repay UI targets by `termId` (= the maturity). That key
 * is internal — not a metadata key — so shared aggregators skip it and it never
 * double-counts against the asset's own row.
 */
export const getExactlyUserDataConverter = (
  _lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lender: string]: UserData } | undefined, number] => {
  const expected = exactlyConfig()?.[chainId]?.previewer ? 1 : 0

  return [
    (data: any[]) => {
      if (!data || data.length < expected || expected === 0) return undefined
      const res = data[0]
      if (!res || res === '0x' || !Array.isArray(res)) return undefined
      const markets = res as ExactlyMarketAccount[]
      const now = nowSec()

      const lenderKey = EXACTLY_LENDER_KEY
      const metaMap: LenderCrossPoolMeta | undefined = meta?.[lenderKey]
      if (!metaMap) return undefined

      const posData: Record<string, any> = {}
      let depositsUSD24h = 0
      let debtUSD24h = 0

      for (const m of markets) {
        const assetAddr = m.asset.toLowerCase()
        const loanUid = createMarketUid(chainId, lenderKey, assetAddr)
        const loanMeta = metaMap[loanUid]
        const decimals = loanMeta?.asset?.decimals ?? m.decimals
        const displayPrice = loanMeta ? getDisplayPrice(loanMeta) : 0
        const oraclePrice = loanMeta ? getOraclePrice(loanMeta) : 0
        const priceHist = loanMeta?.price?.priceUsd24h ?? displayPrice

        const fixedDebtUnits = sumPreview(m.fixedBorrowPositions)
        const depositUnits =
          m.floatingDepositAssets + sumPreview(m.fixedDepositPositions)
        const debtUnits = m.floatingBorrowAssets + fixedDebtUnits
        if (depositUnits === 0n && debtUnits === 0n) continue

        const depositsStr = parseRawAmount(depositUnits.toString(), decimals)
        const depositsNum = Number(depositsStr)
        // Aave-style split on the aggregate: variable `debt` = the floating
        // borrow, fixed `debtStable` = Σ fixed positions (repay-now value).
        const floatingDebtStr = parseRawAmount(
          m.floatingBorrowAssets.toString(),
          decimals,
        )
        const fixedDebtStr = parseRawAmount(fixedDebtUnits.toString(), decimals)
        const debtNum = Number(floatingDebtStr) + Number(fixedDebtStr)

        // Linear per-second late-penalty rate, snapshotted with the read (it is
        // a mutable market parameter).
        const penaltyApr = exactlyPenaltyRateToAprPercent(m.penaltyRate)
        const fixedPositions = [
          ...toDetail(
            m.fixedDepositPositions,
            'deposit',
            now,
            m.penaltyRate,
            penaltyApr,
          ),
          ...toDetail(
            m.fixedBorrowPositions,
            'borrow',
            now,
            m.penaltyRate,
            penaltyApr,
          ),
        ]

        depositsUSD24h += depositsNum * priceHist
        debtUSD24h += debtNum * priceHist

        posData[loanUid] = {
          marketUid: loanUid,
          underlying: assetAddr,
          deposits: depositsStr,
          debt: floatingDebtStr,
          debtStable: fixedDebtStr,
          depositsUSD: depositsNum * displayPrice,
          debtUSD: Number(floatingDebtStr) * displayPrice,
          debtStableUSD: Number(fixedDebtStr) * displayPrice,
          depositsUSDOracle: depositsNum * oraclePrice,
          debtUSDOracle: Number(floatingDebtStr) * oraclePrice,
          debtStableUSDOracle: Number(fixedDebtStr) * oraclePrice,
          stableBorrowRate: '0',
          // The Auditor's per-asset `enterMarket` flag: only entered deposits
          // back debt. A deposit with this false is inert collateral-wise.
          collateralEnabled: m.isCollateral,
          claimableRewards: 0,
          // Per-maturity fixed-position detail (survives to the output — the
          // multi-account state builder mutates posData in place).
          fixedPositions:
            fixedPositions.length > 0 ? fixedPositions : undefined,
        }

        // One PER-LOAN row per fixed borrow, Lista-style: keyed
        // `${loanUid}#${maturity}` (internal key only — not a metadata key, so
        // the shared aggregator skips it → no double-count) with the `term`
        // metadata the repay UI targets by MATURITY (`termId`). `loanId` is the
        // maturity as a string — a stable row key; the actual repay request
        // sends `termId`, never loanId (Exactly has no per-loan ids).
        for (const p of m.fixedBorrowPositions) {
          const maturity = Number(p.maturity)
          const trancheStr = parseRawAmount(p.previewValue.toString(), decimals)
          const isMatured = maturity < now
          const face = faceOf(p)
          const faceStr = parseRawAmount(face.toString(), decimals)
          // Exactly one of these explains repay-now vs face (see toDetail).
          const discount = isMatured
            ? '0'
            : parseRawAmount((face - p.previewValue).toString(), decimals)
          const penalty = isMatured
            ? parseRawAmount((p.previewValue - face).toString(), decimals)
            : '0'
          posData[`${loanUid}#${maturity}`] = {
            marketUid: loanUid,
            underlying: assetAddr,
            loanId: String(maturity),
            deposits: '0',
            debt: '0',
            debtStable: trancheStr,
            depositsUSD: 0,
            debtUSD: 0,
            debtStableUSD: Number(trancheStr) * displayPrice,
            depositsUSDOracle: 0,
            debtUSDOracle: 0,
            debtStableUSDOracle: Number(trancheStr) * oraclePrice,
            collateralEnabled: false,
            claimableRewards: 0,
            term: {
              loanId: String(maturity),
              termId: maturity,
              isDynamic: false,
              // `debt` is the repay-NOW cost (discounted early, penalized when
              // overdue); `faceValue` is what settling AT maturity costs.
              debt: trancheStr,
              faceValue: faceStr,
              maturity,
              accruedInterest: parseRawAmount(
                p.position.fee.toString(),
                decimals,
              ),
              isMatured,
              /** rebate if repaid now, before maturity (never a fee) */
              earlyRepayDiscount: discount,
              /** penalty already accrued past maturity */
              latePenalty: penalty,
              /** further penalty per day overdue — linear on face */
              latePenaltyPerDay: parseRawAmount(
                ((face * m.penaltyRate * DAY_SECONDS) / WAD).toString(),
                decimals,
              ),
              latePenaltyApr: penaltyApr,
              secondsLate: isMatured ? now - maturity : 0,
            },
          }
        }
      }

      if (Object.keys(posData).length === 0) return undefined

      const userData = createMultiAccountTypeUserState(
        {
          chainId,
          account,
          lendingPositions: { '0': posData },
          modes: { '0': 0 },
        } as any,
        metaMap,
        {
          '0': {
            totalDeposits24h: depositsUSD24h,
            totalDebt24h: debtUSD24h,
          },
        },
      )

      if (!userData || userData.data.length === 0) return undefined
      return { [lenderKey]: userData }
    },
    expected,
  ]
}
